import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { MapPin, Check, Loader2, ShieldAlert, RefreshCw, Smartphone, AlertCircle } from 'lucide-react';
import { formatDistance } from '../../lib/utils';
import { GPS_CONFIG } from '../../hooks/useNativeGps';

export const GpsSection = ({
  location, locationError, gettingLocation, gpsRetryCount, gpsWarning,
  locationTimestamp, distance, inRadius, isGpsValid, isDesktopWarning,
  isPegawaiInstansi, onRefresh, mockDetection
}) => {
  return (
    <Card className={`mb-4 ${!isGpsValid && location ? 'border-red-300 bg-red-50' : 'border-indigo-100/50'}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Lokasi GPS
          </span>
          {location && (
            <Button
              size="sm" variant="ghost"
              onClick={() => onRefresh(0)}
              disabled={gettingLocation}
              className="h-7 text-xs"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${gettingLocation ? 'animate-spin' : ''}`} />
              Refresh GPS
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {gettingLocation ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <div>
              <p>Mendapatkan lokasi GPS...</p>
              {gpsRetryCount > 0 && (
                <p className="text-xs text-amber-600">Percobaan ke-{gpsRetryCount + 1} untuk akurasi lebih baik</p>
              )}
            </div>
          </div>
        ) : locationError ? (
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-red-600 text-sm">
              <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">{locationError}</p>
                <p className="text-xs text-red-500 mt-1">Tips: Pastikan GPS aktif dan Anda berada di tempat terbuka.</p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onRefresh(0)} className="w-full border-red-300 text-red-700">
              <RefreshCw className="w-4 h-4 mr-2" /> Coba Lagi
            </Button>
          </div>
        ) : location ? (
          <div className="space-y-3">
            {location.accuracy > GPS_CONFIG.MAX_ACCURACY_METERS ? (
              <Alert className="border-red-200 bg-red-50 py-2">
                <ShieldAlert className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700 text-xs">
                  <strong>Akurasi terlalu rendah!</strong> GPS menunjukkan {Math.round(location.accuracy)}m. Maksimal {GPS_CONFIG.MAX_ACCURACY_METERS}m. Refresh GPS di tempat terbuka.
                </AlertDescription>
              </Alert>
            ) : gpsWarning ? (
              <Alert className="border-amber-200 bg-amber-50 py-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-xs">{gpsWarning}</AlertDescription>
              </Alert>
            ) : (
              <Alert className="border-emerald-200 bg-emerald-50 py-2">
                <Check className="w-4 h-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700 text-xs">
                  GPS akurat ({Math.round(location.accuracy)}m). Lokasi terverifikasi.
                </AlertDescription>
              </Alert>
            )}

            {isDesktopWarning && (
              <Alert className="border-red-200 bg-red-50 py-2">
                <Smartphone className="w-4 h-4 text-red-600" />
                <AlertDescription className="text-red-700 text-xs">
                  <strong>Peringatan Desktop:</strong> GPS dari desktop menggunakan IP location dan tidak akurat. Gunakan perangkat mobile dengan GPS untuk absensi.
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Koordinat</span>
                <span className="font-mono text-xs">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Akurasi</span>
                <span className={`font-medium ${
                  location.accuracy <= GPS_CONFIG.WARNING_ACCURACY_METERS ? 'text-emerald-600' :
                  location.accuracy <= GPS_CONFIG.MAX_ACCURACY_METERS ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {Math.round(location.accuracy)} meter
                </span>
              </div>
              {locationTimestamp && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Waktu GPS</span>
                  <span className="text-xs">{locationTimestamp.toLocaleTimeString('id-ID')}</span>
                </div>
              )}
              {distance !== null && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Jarak dari {isPegawaiInstansi ? 'Instansi' : 'Sekolah'}</span>
                  <div className="flex items-center gap-2">
                    <span>{formatDistance(distance)}</span>
                    <Badge className={inRadius ? 'badge-hadir' : 'badge-mangkir'}>
                      {inRadius ? 'Dalam Radius' : 'Luar Radius'}
                    </Badge>
                  </div>
                </div>
              )}
              {/* Native Anti-Mock GPS Status */}
              {mockDetection && mockDetection.source === 'native' && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Anti-Mock GPS</span>
                  {mockDetection.isSafe ? (
                    <Badge data-testid="mock-detection-safe" className="bg-emerald-100 text-emerald-700 border-emerald-200">
                      <Check className="w-3 h-3 mr-1" /> Aman
                    </Badge>
                  ) : (
                    <Badge data-testid="mock-detection-unsafe" className="bg-red-100 text-red-700 border-red-200">
                      <ShieldAlert className="w-3 h-3 mr-1" /> Risk: {mockDetection.riskScore}/100
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
